import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { recordEvent, ensureSession } from "@/lib/analytics";

/**
 * Customer-facing auth context. Separate concept from admin auth (which lives
 * in AdminLayout's role check). The same auth.users session powers both, but
 * only this context is consumed by the public site / /account routes.
 *
 * Sign-in is OPTIONAL everywhere on the public site. Guests can complete
 * quotes without signing in. When they later sign up using the same email,
 * a Postgres trigger links their guest quote_requests to their new user_id.
 */

type Ctx = {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signInWithPassword: (email: string, password: string) => Promise<{ error: string | null }>;
  signUpWithPassword: (
    email: string,
    password: string,
    metadata?: { full_name?: string; phone?: string },
  ) => Promise<{ error: string | null; needsEmailConfirm: boolean }>;
  resetPassword: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
};

const CustomerAuthContext = createContext<Ctx | null>(null);

export function CustomerAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up listener BEFORE the initial getSession (per Supabase guidance)
    const { data: sub } = supabase.auth.onAuthStateChange((event, next) => {
      setSession(next);
      setUser(next?.user ?? null);
      setLoading(false);
      // Best-effort analytics for auth lifecycle.
      try {
        if (event === "SIGNED_IN") {
          void ensureSession();
          void recordEvent("login", { metadata: { provider: next?.user?.app_metadata?.provider ?? null } });
          // Best-effort: link any guest quote_requests this user submitted
          // (matched by email, server-side). Idempotent — safe to call on
          // every sign-in. Covers the case where an EXISTING user submits a
          // guest quote and then signs in (the signup trigger only fires for
          // brand-new accounts). Never blocks the sign-in flow.
          void supabase.rpc("link_my_guest_requests").then(({ data, error }) => {
            if (error) {
              console.warn("[auth] link_my_guest_requests failed", error);
              return;
            }
            if (typeof data === "number" && data > 0) {
              void recordEvent("guest_requests_linked", { metadata: { count: data } });
            }
          });
        } else if (event === "SIGNED_OUT") {
          void recordEvent("logout");
        }
      } catch {
        /* never break auth on analytics */
      }
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });
    return { error: error?.message ?? null };
  }, []);

  const signUpWithPassword = useCallback(
    async (email: string, password: string, metadata?: { full_name?: string; phone?: string }) => {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/account`,
          data: metadata ?? {},
        },
      });
      if (error) return { error: error.message, needsEmailConfirm: false };
      // If email confirmation is required, session will be null until verified.
      const needsEmailConfirm = !data.session;
      // Fire-and-forget account_created event.
      try {
        void recordEvent("account_created", {
          metadata: { needs_email_confirm: needsEmailConfirm, has_full_name: Boolean(metadata?.full_name) },
        });
      } catch {
        /* ignore */
      }
      return { error: null, needsEmailConfirm };
    },
    [],
  );

  const resetPassword = useCallback(async (email: string) => {
    await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/account/reset-password`,
    });
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<Ctx>(
    () => ({ user, session, loading, signInWithPassword, signUpWithPassword, resetPassword, signOut }),
    [user, session, loading, signInWithPassword, signUpWithPassword, resetPassword, signOut],
  );

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function useCustomerAuth() {
  const ctx = useContext(CustomerAuthContext);
  if (!ctx) throw new Error("useCustomerAuth must be used inside CustomerAuthProvider");
  return ctx;
}